<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  pipelineName: {
    type: String,
    required: true,
  },
  pipelineDescription: {
    type: String,
    default: '',
  },
  totalConversations: {
    type: Number,
    default: 0,
  },
  columnStats: {
    type: Array,
    default: () => [],
  },
  currentCardStatusFilter: {
    type: String,
    default: 'open',
  },
  hasAppliedFilters: {
    type: Boolean,
    default: false,
  },
  pipelines: {
    type: Array,
    default: () => [],
  },
  currentPipeline: {
    type: Object,
    default: null,
  },
  showPipelineDropdown: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'addColumn',
  'updateCardStatusFilter',
  'openConversationFilters',
  'clearAllFilters',
  'selectPipeline',
  'togglePipelineDropdown',
  'createPipeline',
  'managePipelines',
]);

const { t } = useI18n();

const showCardStatusDropdown = ref(false);
const pipelineDropdownRef = ref(null);
const pipelineDropdownPosition = ref({ top: 0, left: 0, width: 200 });

const formattedTotalConversations = computed(() => {
  return props.totalConversations > 999
    ? `${Math.floor(props.totalConversations / 1000)}k+`
    : props.totalConversations.toString();
});

// Opções para filtro de status do card
const cardStatusOptions = computed(() => [
  {
    value: 'open',
    label: 'Em Aberto',
    color: 'text-n-slate-11',
    bgColor: 'bg-n-slate-11/10',
  },
  {
    value: 'won',
    label: 'Ganho',
    color: 'text-n-green-11',
    bgColor: 'bg-n-green-11/10',
  },
  {
    value: 'lost',
    label: 'Perdido',
    color: 'text-n-red-11',
    bgColor: 'bg-n-red-11/10',
  },
  {
    value: 'no_response',
    label: 'Sem Resposta',
    color: 'text-n-orange-11',
    bgColor: 'bg-n-orange-11/10',
  },
  {
    value: 'all',
    label: 'Todos',
    color: 'text-n-blue-11',
    bgColor: 'bg-n-blue-11/10',
  },
]);

const currentCardStatusOption = computed(() => {
  return (
    cardStatusOptions.value.find(
      option => option.value === props.currentCardStatusFilter
    ) || cardStatusOptions.value[0]
  );
});

const toggleCardStatusDropdown = () => {
  showCardStatusDropdown.value = !showCardStatusDropdown.value;
};

const updateCardStatusFilter = status => {
  emit('updateCardStatusFilter', status);
  showCardStatusDropdown.value = false;
};

const togglePipelineDropdown = () => {
  if (!props.showPipelineDropdown && pipelineDropdownRef.value) {
    // Calcular posição do dropdown
    const rect = pipelineDropdownRef.value.getBoundingClientRect();
    pipelineDropdownPosition.value = {
      top: rect.bottom + window.scrollY + 4,
      left: rect.left + window.scrollX,
      width: rect.width,
    };
  }
  emit('togglePipelineDropdown');
};
</script>

<template>
  <div class="border-b border-n-weak bg-n-solid-1">
    <!-- Pipeline info and metrics -->
    <div class="flex items-center justify-between gap-4 px-6 py-3">
      <div
        class="flex items-center gap-3 text-xs text-n-slate-10 overflow-x-auto"
      >
        <!-- Pipeline dropdown -->
        <div class="relative" data-pipeline-dropdown>
          <button
            ref="pipelineDropdownRef"
            class="flex items-center gap-2 px-3 py-2 text-sm bg-n-solid-2 hover:bg-n-solid-3 border border-n-weak rounded-lg text-n-slate-12 transition-colors duration-150 min-w-[200px] justify-between"
            @click="togglePipelineDropdown"
          >
            <div class="flex items-center gap-2">
              <i class="i-lucide-layers size-4 text-n-slate-9" />
              <span class="font-medium truncate">{{
                currentPipeline?.name || t('KANBAN.SELECT_PIPELINE')
              }}</span>
            </div>
            <i
              class="i-lucide-chevron-down size-4 text-n-slate-9 transition-transform duration-150"
              :class="{ 'rotate-180': props.showPipelineDropdown }"
            />
          </button>

          <!-- Dropdown menu -->
          <Teleport to="body">
            <div
              v-if="props.showPipelineDropdown"
              class="fixed bg-n-background border border-n-weak rounded-lg shadow-lg z-[9999] max-h-60 overflow-y-auto"
              :style="{
                top: `${pipelineDropdownPosition.top}px`,
                left: `${pipelineDropdownPosition.left}px`,
                width: `${pipelineDropdownPosition.width}px`,
              }"
            >
              <div class="p-2">
                <div
                  v-for="pipeline in pipelines"
                  :key="pipeline.id"
                  class="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-n-solid-2 cursor-pointer transition-colors duration-150"
                  :class="{
                    'bg-blue-50 border-l-2 border-blue-500':
                      pipeline.id == currentPipeline?.id,
                  }"
                  @click="emit('selectPipeline', pipeline)"
                >
                  <i class="i-lucide-layers size-4 text-n-slate-9" />
                  <div class="flex-1 min-w-0">
                    <div class="font-medium text-n-slate-12 truncate">
                      {{ pipeline.name }}
                    </div>
                    <div
                      v-if="pipeline.description"
                      class="text-xs text-n-slate-11 truncate"
                    >
                      {{ pipeline.description }}
                    </div>
                  </div>
                  <i
                    v-if="pipeline.id == currentPipeline?.id"
                    class="i-lucide-check size-4 text-blue-600"
                  />
                </div>
              </div>

              <div class="border-t border-n-weak p-2">
                <button
                  class="flex items-center gap-2 w-full px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-md transition-colors duration-150"
                  @click="emit('createPipeline')"
                >
                  <i class="i-lucide-plus size-4" />
                  <span>{{ t('KANBAN.PIPELINES.CREATE_SHORT') }}</span>
                </button>
              </div>
            </div>
          </Teleport>

          <!-- Overlay para fechar dropdown de pipeline -->
          <div
            v-if="props.showPipelineDropdown"
            class="fixed inset-0 z-[90]"
            @click="emit('togglePipelineDropdown')"
          />
        </div>

        <!-- Total conversations badge -->
        <div
          class="flex items-center gap-2 px-3 py-2 rounded-lg bg-n-solid-2 whitespace-nowrap"
        >
          <span class="i-lucide-layout-list size-4" />
          <span class="font-medium text-n-slate-12">
            {{ formattedTotalConversations }}
            {{ t('KANBAN.METRICS.TOTAL_CONVERSATIONS') }}
          </span>
        </div>
      </div>

      <!-- Action buttons -->
      <div class="flex items-center gap-2 flex-shrink-0">
        <!-- Conversation Filters Button (Filtro da Esquerda) -->
        <NextButton
          v-tooltip.top-end="'Filtrar Conversas'"
          icon="i-lucide-filter"
          :class="hasAppliedFilters ? 'ruby' : 'slate'"
          xs
          @click="emit('openConversationFilters')"
        >
          Filtrar Conversas
        </NextButton>

        <!-- Remove Filters Button - só aparece quando há filtros aplicados -->
        <NextButton
          v-if="hasAppliedFilters"
          v-tooltip.top-end="'Remover Filtros'"
          icon="i-lucide-x"
          ruby
          xs
          @click="emit('clearAllFilters')"
        >
          Remover Filtros
        </NextButton>

        <!-- Applied Filters Button - Removido para evitar problemas -->

        <!-- Card Status Filter Dropdown (Filtro da Direita) -->
        <div class="relative">
          <NextButton
            v-tooltip.top-end="'Filtrar por Status do Card'"
            icon="i-lucide-filter"
            slate
            xs
            @click="toggleCardStatusDropdown"
          >
            <span class="flex items-center gap-2">
              <span :class="currentCardStatusOption.color">{{
                currentCardStatusOption.label
              }}</span>
              <i
                class="i-lucide-chevron-down size-3 transition-transform"
                :class="{ 'rotate-180': showCardStatusDropdown }"
              />
            </span>
          </NextButton>

          <!-- Card Status Dropdown Menu -->
          <div
            v-if="showCardStatusDropdown"
            class="absolute right-0 top-full mt-1 bg-n-background border border-n-weak rounded-lg shadow-xl py-2 min-w-[160px] z-[100]"
            style="
              box-shadow:
                0 10px 25px rgba(0, 0, 0, 0.1),
                0 4px 6px rgba(0, 0, 0, 0.05);
            "
          >
            <button
              v-for="option in cardStatusOptions"
              :key="option.value"
              class="w-full px-3 py-2 text-left text-sm hover:bg-n-slate-11/10 flex items-center gap-2 transition-colors"
              :class="{
                [option.bgColor + ' ' + option.color]:
                  currentCardStatusFilter === option.value,
              }"
              @click="updateCardStatusFilter(option.value)"
            >
              <div class="w-2 h-2 rounded-full" :class="option.bgColor" />
              <span>{{ option.label }}</span>
            </button>
          </div>

          <!-- Overlay para fechar dropdown -->
          <div
            v-if="showCardStatusDropdown"
            class="fixed inset-0 z-[90]"
            @click="showCardStatusDropdown = false"
          />
        </div>

        <!-- Add column button -->
        <NextButton
          v-tooltip.top-end="t('KANBAN.ADD_COLUMN')"
          icon="i-lucide-plus"
          primary
          xs
          @click="emit('addColumn')"
        >
          {{ t('KANBAN.ADD_COLUMN') }}
        </NextButton>

        <!-- Manage Funils button (ícone) -->
        <NextButton
          v-tooltip.top-end="'Gerenciar Funils'"
          icon="i-lucide-list"
          slate
          xs
          @click="emit('managePipelines')"
        />
      </div>
    </div>
  </div>
</template>
